import type { LessonProgressRow } from '@mentora/data-access'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mentora/ui-components'
import { BookOpen } from 'lucide-react'
import { useTranslations } from 'next-intl'

const PCT = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  maximumFractionDigits: 0,
})

export function PerLessonProgressTable({
  rows,
}: {
  rows: LessonProgressRow[]
}) {
  const t = useTranslations('courseStats.lessonProgress')

  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription>{t('description')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-2 py-10 text-center text-muted-foreground">
          <BookOpen className="size-7" />
          <p className="text-sm">{t('empty')}</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-12 px-4 py-3 font-medium">#</th>
                <th className="px-4 py-3 font-medium">{t('columns.lesson')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('columns.started')}</th>
                <th className="px-4 py-3 text-right font-medium">{t('columns.completed')}</th>
                <th className="w-56 px-4 py-3 font-medium">{t('columns.completionRate')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rate = row.startedCount > 0
                  ? row.completedCount / row.startedCount
                  : 0
                return (
                  <tr
                    key={row.lessonId}
                    className="border-b border-border/40 last:border-b-0"
                  >
                    <td className="px-4 py-3 font-mono tabular-nums text-muted-foreground">
                      {row.position}
                    </td>
                    <td className="px-4 py-3 text-foreground">{row.title}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-muted-foreground">
                      {row.startedCount}
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums text-foreground">
                      {row.completedCount}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-[var(--chart-1)]"
                            style={{ width: `${Math.max(0, Math.min(100, rate * 100))}%` }}
                          />
                        </div>
                        <span className="w-12 shrink-0 text-right font-mono tabular-nums text-muted-foreground">
                          {PCT.format(rate)}
                        </span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
